import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { FiSave, FiX } from 'react-icons/fi';
import './StudentForm.css';

const StudentForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [programList, setProgramList] = useState([]);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [formData, setFormData] = useState({
    nim: '',
    name: '',
    email: '',
    phone: '',
    dateOfBirth: '',
    gender: 'Laki-laki',
    address: '',
    program: '',
    semester: 1,
    gpa: 0,
    enrollmentYear: new Date().getFullYear(),
    status: 'Aktif'
  });

  const fetchProgramList = useCallback(async () => {
    try {
      const res = await axios.get('/api/students/programs');
      const programs = res.data.data || [];
      setProgramList(programs);
      
      // Set default program if not editing and programs exist
      if (!id && programs.length > 0) {
        setFormData(prev => {
          if (!prev.program) {
            return { ...prev, program: programs[0] };
          }
          return prev;
        });
      }
    } catch (error) {
      console.error('Failed to fetch program list:', error);
    }
  }, [id]);

  const fetchStudent = useCallback(async () => {
    try {
      const res = await axios.get(`/api/students/${id}`);
      const student = res.data.data;
      setFormData({
        ...student,
        // Use nama_program_studi if available, otherwise use program
        program: student.nama_program_studi || student.program || '',
        dateOfBirth: student.dateOfBirth ? student.dateOfBirth.split('T')[0] : ''
      });
      
      // Set photo preview if exists
      if (student.photo && student.photo !== 'default-student.png') {
        setPhotoPreview(`/uploads/students/${student.photo}`);
      } else {
        setPhotoPreview('/default-avatar.png');
      }
    } catch (error) {
      toast.error('Gagal mengambil data mahasiswa');
      navigate('/students');
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchProgramList();
    if (id) {
      fetchStudent();
    }
  }, [id, fetchProgramList, fetchStudent]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error('File harus berupa gambar');
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Ukuran file maksimal 5MB');
        return;
      }

      setPhotoFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemovePhoto = () => {
    setPhotoFile(null);
    setPhotoPreview(id ? '/default-avatar.png' : null);
    
    // Reset file input
    const fileInput = document.getElementById('photo-input');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Prepare data - save to both program and nama_program_studi for compatibility
      const dataToSubmit = {
        ...formData,
        nama_program_studi: formData.program // Save to nama_program_studi as well
      };

      let studentId = id;

      if (id) {
        await axios.put(`/api/students/${id}`, dataToSubmit);
      } else {
        const response = await axios.post('/api/students', dataToSubmit);
        studentId = response.data.data._id;
      }

      // Upload photo if selected
      if (photoFile && studentId) {
        const photoFormData = new FormData();
        photoFormData.append('photo', photoFile);
        
        await axios.post(`/api/students/${studentId}/upload-photo`, photoFormData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
      }

      toast.success(id ? 'Data mahasiswa berhasil diupdate' : 'Mahasiswa berhasil ditambahkan');
      
      // Navigate back to detail page if editing, otherwise to list
      if (id) {
        navigate(`/students/${id}`, { replace: true });
      } else {
        navigate('/students');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Terjadi kesalahan');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="student-form-page">
      <div className="page-header">
        <h2>{id ? 'Edit Mahasiswa' : 'Tambah Mahasiswa Baru'}</h2>
      </div>

      <div className="card">
        <form onSubmit={handleSubmit}>
          <div className="form-grid">
            {/* Photo Upload Section */}
            {id && (
              <div className="form-section photo-section">
                <h3>Foto Profil</h3>
                <div className="photo-upload-container">
                  <div className="photo-preview">
                    {photoPreview ? (
                      <img src={photoPreview} alt="Preview" />
                    ) : (
                      <div className="photo-placeholder">
                        <FiSave style={{ fontSize: '48px', opacity: 0.3 }} />
                        <p>Belum ada foto</p>
                      </div>
                    )}
                  </div>
                  <div className="photo-actions">
                    <label htmlFor="photo-input" className="btn btn-outline">
                      Pilih Foto
                    </label>
                    <input
                      id="photo-input"
                      type="file"
                      accept="image/*"
                      onChange={handlePhotoChange}
                      style={{ display: 'none' }}
                    />
                    {photoPreview && photoPreview !== '/default-avatar.png' && (
                      <button
                        type="button"
                        className="btn btn-outline-danger"
                        onClick={handleRemovePhoto}
                      >
                        <FiX /> Hapus
                      </button>
                    )}
                    <small className="text-muted">
                      Format: JPG, PNG, GIF (Max 5MB)
                    </small>
                  </div>
                </div>
              </div>
            )}

            {/* Personal Information */}
            <div className="form-section">
              <h3>Informasi Pribadi</h3>
              
              <div className="form-group">
                <label>NIM *</label>
                <input
                  type="text"
                  name="nim"
                  value={formData.nim}
                  onChange={handleChange}
                  className="form-control"
                  required
                />
              </div>

              <div className="form-group">
                <label>Nama Lengkap *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="form-control"
                  required
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Email *</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="form-control"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Nomor Telepon *</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="form-control"
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Tanggal Lahir *</label>
                  <input
                    type="date"
                    name="dateOfBirth"
                    value={formData.dateOfBirth}
                    onChange={handleChange}
                    className="form-control"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Jenis Kelamin *</label>
                  <select
                    name="gender"
                    value={formData.gender}
                    onChange={handleChange}
                    className="form-control"
                    required
                  >
                    <option value="Laki-laki">Laki-laki</option>
                    <option value="Perempuan">Perempuan</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Alamat *</label>
                <textarea
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  className="form-control"
                  rows="3"
                  required
                ></textarea>
              </div>
            </div>

            {/* Academic Information */}
            <div className="form-section">
              <h3>Informasi Akademik</h3>

              <div className="form-group">
                <label>Program Studi *</label>
                <select
                  name="program"
                  value={formData.program}
                  onChange={handleChange}
                  className="form-control"
                  required
                >
                  <option value="">-- Pilih Program Studi --</option>
                  {programList.map((program, index) => (
                    <option key={index} value={program}>
                      {program}
                    </option>
                  ))}
                </select>
                {programList.length === 0 && (
                  <small className="text-muted">
                    Tidak ada program studi. Silakan sinkronkan data mahasiswa terlebih dahulu.
                  </small>
                )}
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Semester *</label>
                  <input
                    type="number"
                    name="semester"
                    value={formData.semester}
                    onChange={handleChange}
                    className="form-control"
                    min="1"
                    max="14"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>IPK</label>
                  <input
                    type="number"
                    name="gpa"
                    value={formData.gpa}
                    onChange={handleChange}
                    className="form-control"
                    min="0"
                    max="4"
                    step="0.01"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Tahun Masuk *</label>
                  <input
                    type="number"
                    name="enrollmentYear"
                    value={formData.enrollmentYear}
                    onChange={handleChange}
                    className="form-control"
                    min="2000"
                    max={new Date().getFullYear()}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Status *</label>
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    className="form-control"
                    required
                  >
                    <option value="Aktif">Aktif</option>
                    <option value="Cuti">Cuti</option>
                    <option value="Alumni">Alumni</option>
                    <option value="Non-Aktif">Non-Aktif</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="form-actions">
            <button
              type="button"
              className="btn btn-outline"
              onClick={() => navigate('/students')}
            >
              <FiX /> Batal
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              <FiSave /> {loading ? 'Menyimpan...' : 'Simpan'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default StudentForm;
